import SweepStagesModel from '../../../../../models/stagesModel';
import { ConnectStepsListGroup, MetadataElementCurrentStep } from './types';
import { isSalesforceLink, isThirdPartyLink, stageToConnectStepItem } from './helper';
import { FunnelType } from '../../../../../types/enums/FunnelType';
import { CONNECT_STEPS_TAGS_COLORS } from './consts';
import { translateTypeToHumanName } from '../../../../documentation/helpers';

// Gets the groups and steps for the
export const getGroupsForMetadataElement = ({
  step,
  funnelsData,
  thirdPartyFunnelsData,
  metadataElementsData,
  metadataElements,
  getObjectTypeLabel,
}: {
  step: MetadataElementCurrentStep;
  funnelsData: FunnelMap['funnelsData'];
  thirdPartyFunnelsData: FunnelMap['thirdPartyFunnelsData'];
  metadataElementsData: FunnelMap['metadataElementsData'];
  metadataElements: FunnelMap['metadataElements'];
  getObjectTypeLabel: (crmOrgId: string, objectName: string) => string;
}) => {
  if (step.stageId !== undefined) {
    console.error(
      'Unexpected. Stage Id provided in getGroupsForMetadataElement. This isn\t a funnel.',
    );
    return [];
  }
  const metadataElement = metadataElements[step.funnelId];

  const doesNotHaveSfStageFunnelLink = (funnelId: string) => (stage: SweepStage) => {
    return !Boolean(
      metadataElement.funnelLinks
        ?.filter(isSalesforceLink)
        .find((link) => link.funnelId === funnelId && stage._stageId === link.stageId),
    );
  };

  const hasFunnelToFunnelLink = (funnelId: string) => {
    return metadataElement.funnelLinks?.some(
      (link) => link.funnelId === funnelId && link.stageId === undefined,
    );
  };

  const doesNotHaveThirdPartyStageFunnelLink = (funnelId: string) => (step: ThirdPartyStep) => {
    return !Boolean(
      metadataElement.funnelLinks
        ?.filter(isThirdPartyLink)
        .find((link) => link.funnelId === funnelId && step.id === link.stageId),
    );
  };

  const allOtherSfFunnelGroups: ConnectStepsListGroup[] = Object.keys(funnelsData).map(
    (funnelId) => {
      const objectNameLabel = getObjectTypeLabel(
        funnelsData[funnelId].crmOrgId,
        funnelsData[funnelId].funnelDetails.leadingObject.objectName,
      );
      return {
        value: funnelsData[funnelId].id,
        parentName: funnelsData[funnelId].name,
        label: objectNameLabel,
        selectable: !hasFunnelToFunnelLink(funnelId),
        funnelType: FunnelType.SALESFORCE,
        items: new SweepStagesModel(funnelsData[funnelId].funnelDetails.stages)
          .nonLostSteps()
          .filter(doesNotHaveSfStageFunnelLink(funnelId))
          .map(stageToConnectStepItem),
        tag: {
          type: 'object',
          objectName: objectNameLabel,
        },
      };
    },
  );

  const allOtherThirdPartyFunnelGroups: ConnectStepsListGroup[] = Object.keys(
    thirdPartyFunnelsData,
  ).map((funnelId) => ({
    value: thirdPartyFunnelsData[funnelId].id,
    parentName: thirdPartyFunnelsData[funnelId].name,
    label: '3rd party integration',
    selectable: !hasFunnelToFunnelLink(funnelId),
    funnelType: FunnelType.THIRD_PARTY,
    items: thirdPartyFunnelsData[funnelId].steps
      .filter(doesNotHaveThirdPartyStageFunnelLink(funnelId))
      .map((step) => ({
        value: step.id,
        label: step.name,
      })),
    tag: {
      type: 'other',
      tagText: '3rd party integration',
      tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
    },
  }));

  const hasFunnelLinkToMetadataElement = (metadataId: string) => {
    return Boolean(
      metadataElement.funnelLinks?.some(
        (link) => link._type === FunnelType.METADATA_ELEMENT && link.funnelId === metadataId,
      ),
    );
  };

  const allOtherElementGroups: ConnectStepsListGroup[] = Object.entries(metadataElementsData || {})
    .filter(([metadataId]) => metadataId !== step.funnelId)
    .map(([metadataId, metadataElement]) => ({
      value: metadataId,
      parentName: metadataElement.name,
      label: 'Metadata element',
      selectable: !hasFunnelLinkToMetadataElement(metadataId),
      funnelType: FunnelType.METADATA_ELEMENT,
      items: [],
      tag: {
        type: 'other',
        tagText: translateTypeToHumanName(metadataElement.type),
        tagColor: CONNECT_STEPS_TAGS_COLORS.metadataElement,
      },
    }));

  return [...allOtherSfFunnelGroups, ...allOtherThirdPartyFunnelGroups, ...allOtherElementGroups];
};
